import React from 'react'
import {Navigate, Route, Routes} from 'react-router-dom'
import Account from 'pages/account/Account'
import DealerDomains from '../pages/business/dealerDomain/DealerDomains'
import ProtectedRoute from './ProtectedRoute'
import NotFoundPage from '../pages/NotFoundPage'
import AssetViewer from '../pages/assets/viewer/assetViewer'
import AssetEditor from '../pages/assets/editor/assetEditor'
import CreateAsset from '../pages/assets/createAsset'
import AssetsList from '../pages/assets/AssetsList/AssetsList'
import Industries from '../pages/business/industry/Industries'
import Brands from '../pages/business/brand/Brands'
import SalesManagers from 'pages/users/salesManager/SalesManagers'
import Dealers from '../pages/users/dealer/Dealers'
import AccountManagers from '../pages/users/accountManager/AccountManagers'
import Licenses from 'pages/license/Licenses'
import DealerAccount from '../pages/account/DealerAccount'
import Admins from '../pages/users/admin/Admins'
import Companies from '../pages/business/company/Companies'
import LicensePrices from '../pages/license/price/LicensePrices'
import DealerLicenses from '../pages/license/DealerLicenses'
import ActivatedLicenses from '../pages/license/activated/ActivatedLicenses'
import AvailableLicenses from '../pages/license/available/AvailableLicenses'
import ApoDealer from '../pages/apo/dealer/ApoDealer'
import ApoCompany from '../pages/apo/company/ApoCompany'
import DealerProducts from '../pages/products/dealer/DealerProducts'
import DealerProductCreate from '../pages/products/dealer/DealerProductCreate'
import DealerProductEdit from "../pages/products/dealer/DealerProductEdit";
import DealerAnalytics from "../pages/analytics/DealerAnalytics";
import CompanyAnalytics from 'pages/analytics/CompanyAnalytics'
import DealerInvoices from "../pages/invoice/DealerInvoices";
import CompanyInvoices from "../pages/invoice/CompanyInvoices";
import StyleGuides from "../pages/business/styleGuide/StyleGuides";
import DealerPayment from "../pages/payment/DealerPayment";
import CompanyPayment from "../pages/payment/CompanyPayment";
import {useAppSelector} from "../hooks";
import SetupTFA from "../pages/account/SetupTFA";
import {rolePermissionsKey} from "../components/panel/Nav";
import LicensesBilling from "../pages/license/billing/LicensesBilling";
import DealerProductView from "../pages/products/dealer/DealerProductView";
import CompanyProducts from "../pages/products/company/CompanyProducts";
import CompanyProductCreate from "../pages/products/company/CompanyProductCreate";
import CompanyProductEdit from "../pages/products/company/CompanyProductEdit";
import CompanyProductView from "../pages/products/company/CompanyProductView";
import DealerProductsArchived from "../pages/products/dealer/DealerProductsArchived";

const PanelRouter = () => {
    const {role} = useAppSelector(state => state.user)

    return (
        <Routes>
            <Route path="" element={<Navigate to={rolePermissionsKey[role][0]}/>}/>

            <Route path="assets/*" element={<ProtectedRoute allowedRoles={["admin"]}/>}>
                <Route path="*" element={<AssetsList/>}/>
                <Route path={'view/*'} element={<AssetViewer/>}/>
                <Route path={'edit/*'} element={<AssetEditor/>}/>
                <Route path={'create/*'} element={<CreateAsset/>}/>
            </Route>

            <Route path="business">
                <Route path="" element={<NotFoundPage/>}/>

                <Route path="industry" element={<ProtectedRoute element={<Industries/>} allowedRoles={["super admin"]}/>}/>
                <Route path="brand" element={<ProtectedRoute element={<Brands/>} allowedRoles={["super admin"]}/>}/>
                <Route path="company" element={<ProtectedRoute element={<Companies/>} allowedRoles={["super admin", "account manager"]}/>}/>
                <Route path="style-guide" element={<ProtectedRoute element={<StyleGuides/>} allowedRoles={["super admin"]}/>}/>
                <Route path="domains" element={<ProtectedRoute element={<DealerDomains/>} allowedRoles={["dealer"]}/>}/>
            </Route>

            <Route path="users">
                <Route path="" element={<NotFoundPage/>}/>

                <Route path="dealers" element={<ProtectedRoute element={<Dealers/>} allowedRoles={["admin", "account manager"]}/>}/>
                <Route path="admins" element={<ProtectedRoute element={<Admins/>} allowedRoles={["super admin"]}/>}/>
                <Route path="account-managers" element={<ProtectedRoute element={<AccountManagers/>} allowedRoles={["super admin"]}/>}/>
                <Route path="sales-managers" element={<ProtectedRoute element={<SalesManagers/>} allowedRoles={["dealer"]}/>}/>
            </Route>

            <Route path="account">
                <Route path="" element={<ProtectedRoute element={<Account/>} allowedRoles={{except: ["dealer"]}}/>}/>
                <Route path="tfa" element={<ProtectedRoute element={<SetupTFA/>} allowedRoles={"all"}/>}/>
            </Route>

            <Route path="dealer" element={<ProtectedRoute allowedRoles={["dealer"]}/>}>
                <Route path="" element={<NotFoundPage/>}/>

                <Route path="account" element={<DealerAccount/>}/>
                <Route path="invoices" element={<DealerInvoices/>}/>
                <Route path="analytics" element={<DealerAnalytics/>}/>
                <Route path="payment" element={<DealerPayment/>}/>
                <Route path="catalog">
                    <Route path="" element={<DealerProducts/>}/>
                    <Route path="archive" element={<DealerProductsArchived/>}/>
                    <Route path="view/:id" element={<DealerProductView/>}/>
                    <Route path="create" element={<DealerProductCreate/>}/>
                    <Route path="edit/:id" element={<DealerProductEdit/>}/>
                </Route>
            </Route>

            <Route path="company" element={<ProtectedRoute allowedRoles={["admin"]}/>}>
                <Route path="" element={<NotFoundPage/>}/>

                <Route path="invoices" element={<CompanyInvoices/>}/>
                <Route path="analytics" element={<CompanyAnalytics/>}/>
                <Route path="payment" element={<CompanyPayment/>}/>

                <Route path="catalog">
                    <Route path="" element={<CompanyProducts/>}/>
                    <Route path="view/:id" element={<CompanyProductView/>}/>
                    <Route path="create" element={<CompanyProductCreate/>}/>
                    <Route path="edit/:id" element={<CompanyProductEdit/>}/>
                </Route>
            </Route>

            <Route path="invoices" element={<ProtectedRoute allowedRoles={["account manager"]}/>}>
                <Route path="" element={<NotFoundPage/>}/>

                <Route path="dealer/:id" element={<DealerInvoices/>}/>
                <Route path="company/:id" element={<CompanyInvoices/>}/>
            </Route>

            <Route path="apo" element={<ProtectedRoute allowedRoles={["account manager"]}/>}>
                <Route path="" element={<NotFoundPage/>}/>

                <Route path="dealer/:id" element={<ApoDealer/>}/>
                <Route path="company/:id" element={<ApoCompany/>}/>
            </Route>

            <Route path="licenses">
                <Route path="" element={<ProtectedRoute element={<Licenses/>} allowedRoles={["admin", "account manager"]}/>}/>
                <Route path="dealer/:id" element={<ProtectedRoute element={<DealerLicenses/>} allowedRoles={["admin", "account manager"]}/>}/>
                <Route path="activated" element={<ProtectedRoute element={<ActivatedLicenses/>} allowedRoles={["dealer"]}/>}/>
                <Route path="billing" element={<ProtectedRoute element={<LicensesBilling/>} allowedRoles={["dealer"]}/>}/>
                <Route path="available" element={<ProtectedRoute element={<AvailableLicenses/>} allowedRoles={["dealer"]}/>}/>
                <Route path="price/:id" element={<ProtectedRoute element={<LicensePrices/>} allowedRoles={["account manager"]}/>}/>
            </Route>

            <Route path="*" element={<NotFoundPage/>}/>
        </Routes>
    )
}

export default PanelRouter
